/**
 * ShoppingListDialog – shows the shopping list for a given client/date.
 * Compares intake requirements against pantry availability and lists
 * what needs to be bought (missing items) and what is already covered.
 */
import { useMemo } from "react";
import { trpc } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, CheckCircle2, AlertCircle, Package } from "lucide-react";

interface ShoppingListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  klientId: number;
  datum: string;
}

function formatG(g: number): string {
  if (g >= 1000) return `${(g / 1000).toFixed(2)} kg`;
  return `${g.toFixed(0)} g`;
}

function formatRecipeSources(
  sources: { receptNazov: string; neededG: number }[]
): string {
  return sources
    .map(source => `${source.receptNazov} (${formatG(source.neededG)})`)
    .join(", ");
}

export function ShoppingListDialog({
  open,
  onOpenChange,
  klientId,
  datum,
}: ShoppingListDialogProps) {
  const { data: items = [], isLoading } = trpc.spajza.getShoppingList.useQuery(
    { klientId, datum },
    { enabled: open }
  );

  const { missing, covered } = useMemo(() => {
    return {
      missing: items.filter(i => i.missingG > 0),
      covered: items.filter(i => i.missingG === 0),
    };
  }, [items]);

  const handlePrint = () => {
    const lines = [
      `Nákupný zoznam – ${datum}`,
      "=".repeat(40),
      "",
      "CHÝBA (treba kúpiť):",
      ...missing.map(
        i =>
          `  • ${i.nazov}: ${formatG(i.missingG)} (potrebné: ${formatG(i.neededG)}, v špajzi: ${formatG(i.availableG)})${
            i.recipeSources.length > 0
              ? `
      súčasť receptu: ${formatRecipeSources(i.recipeSources)}`
              : ""
          }`
      ),
      "",
      "DOSTUPNÉ V ŠPAJZI:",
      ...covered.map(
        i =>
          `  ✓ ${i.nazov}: ${formatG(i.neededG)} (v špajzi: ${formatG(i.availableG)})${
            i.recipeSources.length > 0
              ? `
      súčasť receptu: ${formatRecipeSources(i.recipeSources)}`
              : ""
          }`
      ),
    ];
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(
        `<pre style="font-family:monospace;padding:20px">${lines.join("\n")}</pre>`
      );
      w.print();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Nákupný zoznam
            <span className="text-sm font-normal text-muted-foreground">
              {datum}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Načítavam…
            </p>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">
                Pre tento deň nie sú žiadne položky v príjme.
              </p>
            </div>
          ) : (
            <>
              {/* Missing items – need to buy */}
              {missing.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-destructive" />
                    <span className="text-sm font-semibold text-destructive">
                      Treba kúpiť ({missing.length})
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {missing.map(item => (
                      <div
                        key={item.potravinaId}
                        className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium truncate">
                            {item.nazov}
                          </span>
                          {item.availableG > 0 && (
                            <p className="text-xs text-muted-foreground">
                              V špajzi: {formatG(item.availableG)} z{" "}
                              {formatG(item.neededG)}
                            </p>
                          )}
                          {item.recipeSources.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Súčasť receptu:{" "}
                              {formatRecipeSources(item.recipeSources)}
                            </p>
                          )}
                        </div>
                        <Badge
                          variant="destructive"
                          className="ml-2 shrink-0 text-xs"
                        >
                          {formatG(item.missingG)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Covered items – already in pantry */}
              {covered.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                      Dostupné v špajzi ({covered.length})
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {covered.map(item => (
                      <div
                        key={item.potravinaId}
                        className="flex items-center justify-between rounded-md border border-green-200 bg-green-50/50 dark:bg-green-950/20 dark:border-green-800 px-3 py-2"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="text-sm truncate">{item.nazov}</span>
                          {item.recipeSources.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Súčasť receptu:{" "}
                              {formatRecipeSources(item.recipeSources)}
                            </p>
                          )}
                        </div>
                        <Badge
                          variant="outline"
                          className="ml-2 shrink-0 text-xs text-green-700 border-green-400"
                        >
                          {formatG(item.neededG)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="pt-4 border-t mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Zavrieť
          </Button>
          {items.length > 0 && (
            <Button variant="outline" onClick={handlePrint}>
              Tlačiť
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
